import { useCallback, useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import { Search, Shield, ShieldOff, LogOut, PlusCircle, MinusCircle, Clock } from "lucide-react";
import api from "../lib/api";
import { extractError } from "../lib/formatError";

const KYC = ["", "none", "pending", "approved", "rejected"];

export default function AdminPlayers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [banned, setBanned] = useState("");
  const [kyc, setKyc] = useState("");
  const [hasReal, setHasReal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (search) params.set("search", search);
      if (banned) params.set("banned", banned);
      if (kyc) params.set("kyc_status", kyc);
      if (hasReal) params.set("has_real_balance", "true");
      const { data } = await api.get(`/admin/players?${params}`);
      setItems(data.items);
      setTotal(data.total);
    } catch (e) { setError(extractError(e)); } finally { setLoading(false); }
    // `search` is intentionally excluded — it's only applied via the submit
    // handler, not on every keystroke. See the <form onSubmit={load}> below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banned, kyc, hasReal]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-players">
      <Toaster theme="dark" position="top-center" />
      <div className="text-[10px] tracking-[0.4em] uppercase text-[color:var(--muted)] mb-2">Players</div>
      <h1 className="font-display italic text-4xl text-[color:var(--ink)] mb-6">All players ({total})</h1>

      <div className="flex flex-wrap gap-3 mb-5">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2">
          <Search className="w-3 h-3 text-[color:var(--muted)]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="email or id"
            className="bet-input text-xs w-56" data-testid="players-search" />
        </form>
        <FilterSelect label="Banned" value={banned} onChange={setBanned} options={["", "true", "false"]} testid="filter-banned" />
        <FilterSelect label="KYC" value={kyc} onChange={setKyc} options={KYC} testid="filter-kyc" />
        <label className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-[color:var(--muted)] cursor-pointer">
          <input type="checkbox" checked={hasReal} onChange={(e) => setHasReal(e.target.checked)} data-testid="filter-has-real" />
          Has real balance
        </label>
      </div>

      {error && <div className="text-xs text-[color:var(--red)] border border-[color:var(--red)] px-3 py-2 mb-3">{error}</div>}
      {loading ? <div className="text-[color:var(--muted)] text-sm">Loading…</div>
        : items.length === 0 ? <div className="text-[color:var(--muted)] text-sm italic">No players match.</div>
        : (
          <div className="border overflow-x-auto" style={{ borderColor: "var(--line)" }}>
            <table className="w-full text-xs">
              <thead className="border-b text-[10px] tracking-[0.25em] uppercase text-[color:var(--muted)]" style={{ borderColor: "var(--line)" }}>
                <tr>
                  <Th>Email</Th><Th>Real</Th><Th>Demo</Th><Th>Deposited</Th><Th>KYC</Th>
                  <Th>Banned</Th><Th>Self-exclude</Th><Th>Last login</Th><Th>Country</Th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ "--tw-divide-opacity": 1 }}>
                {items.map((p) => (
                  <tr key={p.id} onClick={() => setSelected(p.id)} className="hover:bg-[color:var(--card)] cursor-pointer" data-testid={`player-row-${p.id}`}>
                    <Td className="text-[color:var(--ink)]">{p.email}</Td>
                    <Td className="font-mono text-[color:var(--gold)]">${p.real_balance.toFixed(2)}</Td>
                    <Td className="font-mono text-[color:var(--muted)]">${p.demo_balance.toFixed(2)}</Td>
                    <Td className="font-mono">${p.total_deposited_usd.toFixed(2)}</Td>
                    <Td><Pill c={p.kyc_status === "approved" ? "gold" : p.kyc_status === "rejected" ? "red" : "muted"}>{p.kyc_status}</Pill></Td>
                    <Td>{p.banned ? <Pill c="red">yes</Pill> : <span className="text-[color:var(--muted)] text-[10px]">—</span>}</Td>
                    <Td className="text-[10px] text-[color:var(--muted)]">{p.self_excluded_until ? new Date(p.self_excluded_until).toLocaleDateString() : "—"}</Td>
                    <Td className="text-[10px] text-[color:var(--muted)]">{p.last_login_at ? new Date(p.last_login_at).toLocaleString() : "never"}</Td>
                    <Td className="uppercase tracking-widest text-[10px]">{p.country_code || "—"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {selected && (
        <PlayerDetailModal id={selected} onClose={() => setSelected(null)} onChanged={() => { load(); }} />
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-[color:var(--muted)]">
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} className="bg-[color:var(--shell-2)] border border-[color:var(--line)] px-2 py-1 text-[color:var(--ink)]" data-testid={testid}>
        {options.map((o) => <option key={o} value={o}>{o || "all"}</option>)}
      </select>
    </label>
  );
}

function Th({ children }) { return <th className="text-left px-3 py-2 font-normal">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2 ${className}`}>{children}</td>; }
function Pill({ children, c = "muted" }) {
  return <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 border" style={{ color: `var(--${c})`, borderColor: `var(--${c})` }}>{children}</span>;
}

const TABS = ["overview", "transactions", "rounds", "deposits", "withdrawals", "kyc", "rg"];

function PlayerDetailModal({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState(false);
  const [adjust, setAdjust] = useState({ mode: "real", delta_usd: "", note: "" });
  const [excludePeriod, setExcludePeriod] = useState("7d");
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let mounted = true;
    api.get(`/admin/players/${id}`).then(({ data }) => mounted && setData(data)).catch((e) => setError(extractError(e)));
    return () => { mounted = false; };
  }, [id, refreshTick]);

  const call = async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); onChanged(); setRefreshTick((t) => t + 1); }
    catch (e) { setError(extractError(e)); }
    finally { setBusy(false); }
  };

  if (!data && !error) return <ModalShell onClose={onClose}><div className="text-[color:var(--muted)] p-6">Loading…</div></ModalShell>;
  if (error && !data) return <ModalShell onClose={onClose}><div className="text-[color:var(--red)] p-6">{error}</div></ModalShell>;

  const p = data.player;
  return (
    <ModalShell onClose={onClose} testid="player-detail">
      <header className="p-5 border-b flex justify-between items-start gap-4" style={{ borderColor: "var(--line)" }}>
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--muted)]">Player</div>
          <div className="font-display italic text-2xl text-[color:var(--gold)]">{p.email}</div>
          <div className="text-[10px] text-[color:var(--muted)] font-mono mt-1">{p.id}</div>
        </div>
        <div className="text-right text-[10px] tracking-widest uppercase text-[color:var(--muted)]">
          <div>Real: <span className="text-[color:var(--gold)] font-mono">${p.real_balance.toFixed(2)}</span></div>
          <div>Demo: <span className="font-mono">${p.demo_balance.toFixed(2)}</span></div>
          <div>Total deposited: ${p.total_deposited_usd.toFixed(2)}</div>
          <div>KYC: {p.kyc_status}</div>
        </div>
      </header>

      <nav className="border-b flex gap-1 overflow-x-auto px-3" style={{ borderColor: "var(--line)" }}>
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-2 text-[10px] tracking-widest uppercase border-b-2 -mb-px ${tab === t ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-transparent text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`} data-testid={`player-tab-${t}`}>{t}</button>
        ))}
      </nav>

      <div className="flex-1 overflow-y-auto p-5">
        {error && <div className="text-xs text-[color:var(--red)] border border-[color:var(--red)] px-3 py-2 mb-3">{error}</div>}

        {tab === "overview" && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Stat label="Total wagered" value={`$${data.stats.total_wagered.toFixed(2)}`} />
              <Stat label="Total won" value={`$${data.stats.total_won.toFixed(2)}`} />
              <Stat label="GGR (this player)" value={`$${data.stats.ggr.toFixed(2)}`} />
            </div>

            <Section title="Actions">
              <div className="flex flex-wrap gap-2 mb-4">
                {p.banned
                  ? <button onClick={() => call(() => api.post(`/admin/players/${p.id}/unban`))} disabled={busy} className="px-3 py-2 border border-[color:var(--gold)] text-[color:var(--gold)] text-[10px] tracking-widest uppercase" data-testid="player-unban-btn"><Shield className="w-3 h-3 inline mr-1" />Unban</button>
                  : <button onClick={() => call(() => api.post(`/admin/players/${p.id}/ban`))} disabled={busy} className="px-3 py-2 border border-[color:var(--red)] text-[color:var(--red)] text-[10px] tracking-widest uppercase" data-testid="player-ban-btn"><ShieldOff className="w-3 h-3 inline mr-1" />Ban</button>
                }
                <button onClick={() => call(() => api.post(`/admin/players/${p.id}/force-logout`))} disabled={busy} className="px-3 py-2 border border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--red)] hover:border-[color:var(--red)] text-[10px] tracking-widest uppercase" data-testid="player-force-logout-btn">
                  <LogOut className="w-3 h-3 inline mr-1" />Force logout
                </button>
              </div>

              <div className="border p-4 mb-4" style={{ borderColor: "var(--line)", background: "var(--shell-2)" }}>
                <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] mb-2">Adjust balance</div>
                <div className="flex flex-wrap gap-2 items-end">
                  <label className="text-[10px] tracking-widest uppercase text-[color:var(--muted)]">
                    Mode
                    <select value={adjust.mode} onChange={(e) => setAdjust({ ...adjust, mode: e.target.value })} className="ml-2 bg-[color:var(--shell-2)] border border-[color:var(--line)] px-2 py-1 text-[color:var(--ink)]" data-testid="adjust-mode">
                      <option>real</option><option>demo</option>
                    </select>
                  </label>
                  <input type="number" step="0.01" value={adjust.delta_usd} onChange={(e) => setAdjust({ ...adjust, delta_usd: e.target.value })} placeholder="±USD" className="bet-input w-28 text-xs" data-testid="adjust-delta" />
                  <input value={adjust.note} onChange={(e) => setAdjust({ ...adjust, note: e.target.value })} placeholder="Note" className="bet-input flex-1 text-xs" data-testid="adjust-note" />
                  <button
                    disabled={busy || !adjust.delta_usd || !adjust.note.trim()}
                    onClick={() => call(() => api.post(`/admin/players/${p.id}/adjust-balance`, { mode: adjust.mode, delta_usd: Number(adjust.delta_usd), note: adjust.note.trim() }).then(() => setAdjust({ mode: adjust.mode, delta_usd: "", note: "" })))}
                    className="px-3 py-2 border border-[color:var(--gold)] text-[color:var(--gold)] text-[10px] tracking-widest uppercase disabled:opacity-40"
                    data-testid="adjust-submit"
                  >
                    Apply
                  </button>
                </div>
              </div>

              <div className="border p-4" style={{ borderColor: "var(--line)", background: "var(--shell-2)" }}>
                <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] mb-2">Extend self-exclusion</div>
                <div className="flex flex-wrap gap-2 items-end">
                  <select value={excludePeriod} onChange={(e) => setExcludePeriod(e.target.value)} className="bg-[color:var(--shell-2)] border border-[color:var(--line)] px-2 py-1 text-[color:var(--ink)] text-xs" data-testid="excl-period">
                    <option value="24h">24h</option><option value="7d">7d</option><option value="30d">30d</option><option value="permanent">permanent</option>
                  </select>
                  <button
                    onClick={() => call(() => api.post(`/admin/players/${p.id}/extend-self-exclusion`, { period: excludePeriod }))}
                    disabled={busy}
                    className="px-3 py-2 border border-[color:var(--red)] text-[color:var(--red)] text-[10px] tracking-widest uppercase"
                    data-testid="excl-submit"
                  >
                    <Clock className="w-3 h-3 inline mr-1" />Extend
                  </button>
                  <span className="text-[9px] tracking-widest text-[color:var(--muted)]">Cannot shorten.</span>
                </div>
              </div>
            </Section>
          </div>
        )}

        {tab === "transactions" && <SimpleTable rows={data.transactions} cols={["created_at", "type", "mode", "amount", "balance_after", "note"]} />}
        {tab === "rounds" && <SimpleTable rows={data.rounds} cols={["created_at", "mode", "bet_amount", "color_pick", "correct_color", "won", "payout", "status"]} />}
        {tab === "deposits" && <SimpleTable rows={data.deposits} cols={["created_at", "provider", "amount_usd", "status", "tx_hash"]} />}
        {tab === "withdrawals" && <SimpleTable rows={data.withdrawals} cols={["created_at", "amount_usd", "crypto_currency", "crypto_address", "status", "tx_hash", "reject_reason"]} />}
        {tab === "kyc" && <SimpleTable rows={data.kyc} cols={["submitted_at", "doc_type", "status", "reject_reason", "reviewed_at"]} />}
        {tab === "rg" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Stat label="Daily limit" value={`$${p.rg.daily_limit_usd.toFixed(0)}`} />
            <Stat label="Weekly limit" value={`$${p.rg.weekly_limit_usd.toFixed(0)}`} />
            <Stat label="Monthly limit" value={`$${p.rg.monthly_limit_usd.toFixed(0)}`} />
            <Stat label="Session alert" value={`${p.rg.session_alert_minutes} min`} />
            <div className="sm:col-span-2 border p-3" style={{ borderColor: "var(--line)" }}>
              <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] mb-1">Pending increases</div>
              {(p.rg.pending_increases || []).length === 0
                ? <div className="text-xs text-[color:var(--muted)] italic">None.</div>
                : <ul className="text-[10px] font-mono text-[color:var(--muted)] space-y-1">
                    {p.rg.pending_increases.map((it) => (
                      <li key={it.id}>
                        effective {new Date(it.effective_at).toLocaleString()} — daily:{it.new_daily} weekly:{it.new_weekly} monthly:{it.new_monthly}
                      </li>
                    ))}
                  </ul>
              }
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

function ModalShell({ children, onClose, testid = "modal" }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6" style={{ background: "rgba(6,4,3,0.85)", backdropFilter: "blur(4px)" }} data-testid={testid}>
      <div className="w-full max-w-4xl h-[80vh] border flex flex-col" style={{ borderColor: "var(--line)", background: "var(--card)" }}>
        <button onClick={onClose} className="absolute top-8 right-8 text-[color:var(--muted)] hover:text-[color:var(--ink)] text-2xl" data-testid="modal-close">×</button>
        {children}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] mb-3">{title}</div>
      {children}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border p-3" style={{ borderColor: "var(--line)" }}>
      <div className="text-[9px] tracking-widest uppercase text-[color:var(--muted)]">{label}</div>
      <div className="text-lg font-mono text-[color:var(--ink)]">{value}</div>
    </div>
  );
}

function SimpleTable({ rows, cols }) {
  if (!rows || rows.length === 0) return <div className="text-xs text-[color:var(--muted)] italic">Empty.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="border-b text-[10px] tracking-widest uppercase text-[color:var(--muted)]" style={{ borderColor: "var(--line)" }}>
          <tr>{cols.map((c) => <th key={c} className="text-left px-2 py-2 font-normal">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-b" style={{ borderColor: "var(--line)" }}>
              {cols.map((c) => {
                let v = r[c];
                if (v === null || v === undefined) v = "—";
                if (typeof v === "boolean") v = v ? "✓" : "—";
                if (typeof v === "object") v = JSON.stringify(v);
                if (typeof v === "string" && v.length > 30 && (c.includes("id") || c.includes("hash") || c.includes("address"))) v = v.slice(0, 12) + "…";
                return <td key={c} className="px-2 py-1.5 text-[color:var(--ink)] text-[11px] break-all">{String(v)}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
